// src/components/lekhi/LekhiSundayTestPage.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getSundayTestQuestions,
  getOrCreateTestProgress,
  saveTestAnswer,
  finalizeSundayTest,
  checkAndUnlockAchievements
} from '../../services/firebase/firestoreService';
import { getSubjectName } from '../../services/lekhiTopicNames';
import {
  renderLekhiText,
  LekhiWatermark,
  lekhiProtectProps,
  LEKHI_PROTECT_CLASS
} from './LekhiProtect';
import './LekhiSundayTestPage.css';

const SUBJECTS = ['grammar', 'math', 'reasoning', 'gk'];

const TEXT = {
  title: { mr: 'आजची परीक्षा', en: "Today's Test" },
  introHeading: { mr: 'सूचना वाचा', en: 'Read the instructions' },
  introPoints: {
    mr: [
      'एकूण ६० प्रश्न (४ विषय × १५ प्रश्न)',
      'वेळ ६० मिनिटे',
      'प्रश्न सोडता येतात — कोणताही प्रश्न रिकामा ठेवू शकता',
      'रिकाम्या प्रश्नांना शून्य गुण मिळतील',
      'एकदाच परीक्षा देता येते — पुन्हा संधी मिळणार नाही',
      '५ मिनिटे शिल्लक असताना घड्याळ आपोआप दिसेल'
    ],
    en: [
      'Total 60 questions (4 subjects × 15)',
      '60 minutes duration',
      'You can skip questions — leave any question empty',
      'Empty questions get zero marks',
      'One attempt only — no retakes',
      'Timer auto-shows when 5 minutes remain'
    ]
  },
  timerWarnHeading: { mr: '⚠️ महत्त्वाची सूचना', en: '⚠️ Important' },
  timerWarnBody: {
    mr: 'परीक्षा सुरू केल्यानंतर ६० मिनिटांचे घड्याळ सुरू होते आणि ते कधीही थांबत नाही. तुम्ही अ‍ॅप बंद केले, मागे गेलात, किंवा फोन ठेवला तरी वेळ चालूच राहील. वेळ संपल्यावर उरलेल्या प्रश्नांना शून्य गुण मिळतील. त्यामुळे ६० मिनिटे मोकळी असतील तेव्हाच परीक्षा सुरू करा.',
    en: 'Once you start, the 60-minute clock begins and never stops. Even if you close the app, go back, or put your phone away, the time keeps running. Any questions left when time ends get zero marks. Start only when you have a free 60 minutes.'
  },
  startBtn: { mr: 'परीक्षा सुरू करा', en: 'Start Test' },
  prevBtn: { mr: 'मागील विषय', en: 'Previous Subject' },
  nextBtn: { mr: 'पुढील विषय', en: 'Next Subject' },
  submitBtn: { mr: 'परीक्षा सबमिट करा', en: 'Submit Test' },
  loading: { mr: 'परीक्षा तयार होत आहे...', en: 'Loading test...' },
  loadError: { mr: 'परीक्षा आणता आली नाही. पुन्हा प्रयत्न कर.', en: 'Could not load test. Try again.' },
  noQuestions: { mr: 'आजच्या परीक्षेसाठी प्रश्न उपलब्ध नाहीत.', en: 'No questions available for today.' },
  submitting: { mr: 'सबमिट होत आहे...', en: 'Submitting...' },
  quitConfirmTitle: { mr: '⚠️ थांबा! वेळ थांबणार नाही', en: '⚠️ Wait! Time will NOT pause' },
  quitConfirmBody: {
    mr: 'तुम्ही बाहेर पडलात तरी परीक्षेचे घड्याळ चालूच राहील. ते थांबत नाही. वेळ संपल्यावर उरलेल्या सर्व प्रश्नांना शून्य गुण मिळतील. खरंच बाहेर पडायचे?',
    en: 'The test clock keeps running even after you leave. It does NOT pause. When the time ends, every unanswered question gets zero marks. Really exit?'
  },
  quitYes: { mr: 'तरीही बाहेर पडा', en: 'Exit anyway' },
  quitNo: { mr: 'परीक्षेत राहा', en: 'Stay in test' },
  submitConfirmTitle: { mr: 'थांबा!', en: 'Wait!' },
  submitConfirmBody: {
    mr: 'प्रश्न उत्तराशिवाय आहेत. सबमिट केल्यावर बदल करता येणार नाहीत.',
    en: 'questions are without answers. After submit, no changes possible.'
  },
  submitYes: { mr: 'सबमिट करा', en: 'Submit' },
  submitNo: { mr: 'मागे जा', en: 'Go back' },
  timeUpTitle: { mr: 'थांबा!', en: 'Wait!' },
  timeUpBody: {
    mr: '६० मिनिटे संपली. परीक्षा सबमिट होत आहे.',
    en: '60 minutes done. Test will be submitted.'
  },
  timeUpSubmit: { mr: 'सबमिट करा', en: 'Submit' },
  timeUpExtend: { mr: '६० सेकंद द्या', en: 'Give 60 seconds' },
  autoSubmittedTitle: { mr: 'परीक्षा सबमिट झाली', en: 'Test submitted' },
  autoSubmittedBody: {
    mr: 'वेळ संपल्यामुळे परीक्षा आपोआप सबमिट झाली.',
    en: 'Test was auto-submitted because time ran out.'
  },
  expiredTitle: { mr: 'तुमची वेळ संपली आहे', en: 'Your time has ended' },
  expiredBody: {
    mr: 'तुम्ही परीक्षा सुरू केली होती आणि ६० मिनिटांचे घड्याळ त्याच वेळी सुरू झाले होते. तुम्ही परीक्षेतून बाहेर पडल्यानंतरही ते चालू राहिले आणि आता संपले आहे. त्यामुळे आता आणखी प्रश्न सोडवता येणार नाहीत.',
    en: 'You had started the test, and the 60-minute clock started at that moment. It kept running after you left the test and has now ended. You cannot answer any more questions.'
  },
  expiredAnsweredLabel: { mr: 'तुम्ही सोडवलेले प्रश्न', en: 'Questions you answered' },
  expiredNote: {
    mr: 'खालील बटण दाबल्यावर तुमची उत्तरे तपासली जातील आणि निकाल दिसेल. उरलेल्या प्रश्नांना शून्य गुण मिळतील.',
    en: 'Tap the button below to have your answers checked and see your result. Unanswered questions get zero marks.'
  },
  expiredSubmitBtn: { mr: 'निकाल पाहण्यासाठी सबमिट करा', en: 'Submit to see result' },
  resultsTitle: { mr: 'निकाल', en: 'Results' },
  totalScore: { mr: 'एकूण गुण', en: 'Total Score' },
  thSubject: { mr: 'विषय', en: 'Subject' },
  thScore: { mr: 'गुण', en: 'Score' },
  reviewBtn: { mr: 'विश्लेषण करा', en: 'Review Test' },
  goHomeBtn: { mr: 'पुढे जा', en: 'Go Home' },
  reviewPrevBtn: { mr: 'मागील विषय', en: 'Previous' },
  reviewNextBtn: { mr: 'पुढील विषय', en: 'Next' },
  reviewDoneBtn: { mr: 'मी पाहिले', en: 'Done' },
  yourAnswer: { mr: 'तुझे उत्तर', en: 'Your answer' },
  correctAnswer: { mr: 'योग्य उत्तर', en: 'Correct answer' },
  emptyAnswer: { mr: 'रिकामे', en: 'Empty' },
  explanation: { mr: 'स्पष्टीकरण', en: 'Explanation' }
};

const formatTime = (totalSeconds) => {
  if (totalSeconds < 0) totalSeconds = 0;
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const warnBoxStyle = {
  background: '#fff4e5',
  border: '1.5px solid #f59e0b',
  borderRadius: '10px',
  padding: '14px 16px',
  margin: '0 0 20px 0'
};

const warnHeadingStyle = {
  fontSize: '15px',
  fontWeight: 800,
  color: '#92400e',
  marginBottom: '8px'
};

const warnBodyStyle = {
  fontSize: '14px',
  lineHeight: 1.6,
  color: '#7c3a06',
  margin: 0
};

const PROTECTED_CONTAINER_CLASS = `sun-container ${LEKHI_PROTECT_CLASS}`;

export default function LekhiSundayTestPage({ testNumber, onBack }) {
  const { currentUser, userData } = useAuth();
  const language = localStorage.getItem('spocademy_language') || 'mr';
  const t = (key) => TEXT[key][language] || TEXT[key].mr;

  const [stage, setStage] = useState('intro');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [noQuestions, setNoQuestions] = useState(false);

  const [questionsBySubject, setQuestionsBySubject] = useState(null);
  const [flatQuestions, setFlatQuestions] = useState([]);
  const [subjectIndex, setSubjectIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const [endsAtMs, setEndsAtMs] = useState(null);
  const [remainingSec, setRemainingSec] = useState(60 * 60);
  const [timerHidden, setTimerHidden] = useState(false);

  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showTimeUpPopup, setShowTimeUpPopup] = useState(false);
  const [extensionUsed, setExtensionUsed] = useState(false);
  const [showAutoSubmitted, setShowAutoSubmitted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [reviewSubjectIndex, setReviewSubjectIndex] = useState(0);

  const timerIntervalRef = useRef(null);
  const finalizingRef = useRef(false);

  const userLevels = useMemo(() => {
    if (!userData) return null;
    return {
      grammar: userData.grammarLevel || 'easy',
      math: userData.mathLevel || 'easy',
      reasoning: userData.reasoningLevel || 'easy',
      gk: userData.gkLevel || 'easy'
    };
  }, [userData]);

  const triggerAchievementCheck = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const newlyUnlocked = await checkAndUnlockAchievements(currentUser.uid);
      if (newlyUnlocked.length > 0) {
        const key = `newAchievements_${currentUser.uid}`;
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        localStorage.setItem(key, JSON.stringify([...existing, ...newlyUnlocked.map(a => a.id)]));

        const celebrateOnes = newlyUnlocked.filter(a => a.celebrate === true);
        if (celebrateOnes.length > 0) {
          const celKey = `celebrateAchievements_${currentUser.uid}`;
          const existingCel = JSON.parse(localStorage.getItem(celKey) || '[]');
          const toStore = celebrateOnes.map(a => ({
            id: a.id, category: a.category, title: a.title, description: a.description
          }));
          localStorage.setItem(celKey, JSON.stringify([...existingCel, ...toStore]));
        }
      }
    } catch (e) {}
  }, [currentUser]);

  const handleStart = async () => {
    if (!currentUser?.uid || !testNumber || !userLevels) return;
    setLoading(true);
    setLoadError(false);
    setNoQuestions(false);
    try {
      const fetched = await getSundayTestQuestions(testNumber, userLevels);
      if (!fetched || fetched.length === 0) {
        setNoQuestions(true);
        setLoading(false);
        return;
      }
      const bySubject = { grammar: [], math: [], reasoning: [], gk: [] };
      fetched.forEach(q => {
        if (bySubject[q.subject]) bySubject[q.subject].push(q);
      });
      const ordered = [
        ...bySubject.grammar,
        ...bySubject.math,
        ...bySubject.reasoning,
        ...bySubject.gk
      ];

      const { progressDoc, isResume, expired } = await getOrCreateTestProgress(
        currentUser.uid,
        testNumber,
        ordered
      );

      // Time ran out while the user was away. Do NOT auto-submit.
      // Show a clear explanation and let the user submit manually.
      if (expired) {
        setQuestionsBySubject(bySubject);
        setFlatQuestions(ordered);
        setAnswers(progressDoc.answers || {});
        setStage('expired');
        setLoading(false);
        return;
      }

      let endsAtMillis;
      if (progressDoc.endsAt?.seconds) {
        endsAtMillis = progressDoc.endsAt.seconds * 1000;
      } else if (progressDoc.endsAt instanceof Date) {
        endsAtMillis = progressDoc.endsAt.getTime();
      } else {
        endsAtMillis = Date.now() + 60 * 60 * 1000;
      }

      setQuestionsBySubject(bySubject);
      setFlatQuestions(ordered);
      setAnswers(isResume ? (progressDoc.answers || {}) : {});
      setEndsAtMs(endsAtMillis);
      setStage('test');
      setLoading(false);
    } catch (e) {
      setLoadError(true);
      setLoading(false);
    }
  };

  // Timer tick
  useEffect(() => {
    if (stage !== 'test' || !endsAtMs) return;

    const tick = () => {
      const now = Date.now();
      const remaining = Math.floor((endsAtMs - now) / 1000);
      setRemainingSec(remaining);
      if (remaining <= 300) setTimerHidden(false);
      if (remaining <= 0) {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        if (!showTimeUpPopup && !extensionUsed) {
          setShowTimeUpPopup(true);
        } else if (extensionUsed) {
          autoSubmitFromExpiry();
        }
      }
    };

    tick();
    timerIntervalRef.current = setInterval(tick, 1000);
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, endsAtMs, extensionUsed]);

  const autoSubmitFromExpiry = useCallback(async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setShowTimeUpPopup(false);
    try {
      const res = await finalizeSundayTest(currentUser.uid, testNumber, flatQuestions);
      setResults(res);
      setShowAutoSubmitted(true);
      triggerAchievementCheck();
    } catch (e) {
      finalizingRef.current = false;
    }
  }, [currentUser, testNumber, flatQuestions, triggerAchievementCheck]);

  const handleAnswerSelect = async (qId, optionLetter) => {
    setAnswers(prev => ({ ...prev, [qId]: optionLetter }));
    try {
      await saveTestAnswer(currentUser.uid, testNumber, qId, optionLetter);
    } catch (e) {}
  };

  const handleNextSubject = () => {
    setSubjectIndex(i => Math.min(i + 1, SUBJECTS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevSubject = () => {
    setSubjectIndex(i => Math.max(i - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmitClick = () => {
    setShowSubmitConfirm(true);
  };

  const handleSubmitConfirm = async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setShowSubmitConfirm(false);
    setSubmitting(true);
    try {
      const res = await finalizeSundayTest(currentUser.uid, testNumber, flatQuestions);
      setResults(res);
      setStage('results');
      triggerAchievementCheck();
    } catch (e) {
      finalizingRef.current = false;
      setSubmitting(false);
    }
  };

  // Manual submit from the expired screen
  const handleExpiredSubmit = async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setSubmitting(true);
    try {
      const res = await finalizeSundayTest(currentUser.uid, testNumber, flatQuestions);
      setResults(res);
      setStage('results');
      setSubmitting(false);
      triggerAchievementCheck();
    } catch (e) {
      finalizingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleExtend = () => {
    setExtensionUsed(true);
    setShowTimeUpPopup(false);
    const newEnd = Date.now() + 60 * 1000;
    setEndsAtMs(newEnd);
    setTimerHidden(false);
  };

  const handleTimeUpSubmit = async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setShowTimeUpPopup(false);
    setSubmitting(true);
    try {
      const res = await finalizeSundayTest(currentUser.uid, testNumber, flatQuestions);
      setResults(res);
      setStage('results');
      triggerAchievementCheck();
    } catch (e) {
      finalizingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleAutoSubmittedOk = () => {
    setShowAutoSubmitted(false);
    setStage('results');
  };

  const handleBackTap = () => {
    if (stage === 'intro') {
      onBack(false);
      return;
    }
    if (stage === 'test') {
      setShowQuitConfirm(true);
      return;
    }
  };

  const handleQuitYes = () => {
    setShowQuitConfirm(false);
    onBack(false);
  };

  const handleQuitNo = () => {
    setShowQuitConfirm(false);
  };

  const handleReviewStart = () => {
    setReviewSubjectIndex(0);
    setStage('review');
  };

  const handleReviewDone = () => {
    onBack(true);
  };

  const handleResultsGoHome = () => {
    onBack(true);
  };

  // ===== INTRO SCREEN =====
  if (stage === 'intro') {
    return (
      <div className={PROTECTED_CONTAINER_CLASS} {...lekhiProtectProps}>
        <LekhiWatermark />
        <div className="sun-header">
          <button className="sun-back-btn" onClick={handleBackTap}>←</button>
          <div className="sun-header-title">{t('title')}</div>
          <div style={{ width: 36 }} />
        </div>
        <div className="sun-content">
          <div className="sun-intro">
            <h2>{t('introHeading')}</h2>
            <ul>
              {t('introPoints').map((p, i) => <li key={i}>{p}</li>)}
            </ul>

            <div style={warnBoxStyle}>
              <div style={warnHeadingStyle}>{t('timerWarnHeading')}</div>
              <p style={warnBodyStyle}>{t('timerWarnBody')}</p>
            </div>

            {loadError && <div className="sun-status sun-status-error">{t('loadError')}</div>}
            {noQuestions && <div className="sun-status sun-status-error">{t('noQuestions')}</div>}
            {!noQuestions && (
              <button className="sun-btn sun-btn-primary" style={{ width: '100%' }}
                onClick={handleStart} disabled={loading}>
                {loading ? t('loading') : t('startBtn')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== EXPIRED SCREEN (time ran out while user was away) =====
  if (stage === 'expired') {
    const answeredCount = flatQuestions.filter(q => answers[q.id]).length;
    return (
      <div className={PROTECTED_CONTAINER_CLASS} {...lekhiProtectProps}>
        <LekhiWatermark />
        <div className="sun-header">
          <div style={{ width: 36 }} />
          <div className="sun-header-title">{t('title')}</div>
          <div style={{ width: 36 }} />
        </div>
        <div className="sun-content">
          <div className="sun-intro">
            <h2 style={{ color: '#dc3545' }}>{t('expiredTitle')}</h2>

            <div style={warnBoxStyle}>
              <p style={warnBodyStyle}>{t('expiredBody')}</p>
            </div>

            <div style={{
              textAlign: 'center',
              fontSize: '15px',
              color: '#444',
              marginBottom: '18px'
            }}>
              {t('expiredAnsweredLabel')}:{' '}
              <strong style={{ fontSize: '20px', color: '#6366f1' }}>
                {answeredCount} / {flatQuestions.length}
              </strong>
            </div>

            <p style={{
              fontSize: '14px',
              lineHeight: 1.6,
              color: '#555',
              marginBottom: '20px'
            }}>
              {t('expiredNote')}
            </p>

            <button className="sun-btn sun-btn-submit" style={{ width: '100%' }}
              onClick={handleExpiredSubmit} disabled={submitting}>
              {submitting ? t('submitting') : t('expiredSubmitBtn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== TEST SCREEN =====
  if (stage === 'test') {
    const currentSubject = SUBJECTS[subjectIndex];
    const currentQs = questionsBySubject?.[currentSubject] || [];
    const isFirstSubject = subjectIndex === 0;
    const isLastSubject = subjectIndex === SUBJECTS.length - 1;
    const lockedTimer = remainingSec <= 300;

    return (
      <div className={PROTECTED_CONTAINER_CLASS} {...lekhiProtectProps}>
        <LekhiWatermark />
        <div className="sun-header">
          <button className="sun-back-btn" onClick={handleBackTap}>←</button>
          <div className="sun-header-title">{t('title')}</div>
          <div className="sun-progress-bar">
            {SUBJECTS.map((s, i) => (
              <div key={s}
                className={`sun-progress-segment ${
                  i < subjectIndex ? 'filled' : i === subjectIndex ? 'active' : ''
                }`} />
            ))}
          </div>
        </div>

        <div className="sun-content">
          <div className="sun-subject-heading">
            {getSubjectName(currentSubject, language)}
          </div>

          {currentQs.map((q, idx) => (
            <div key={q.id} className="sun-question-card">
              <div className="sun-question-number">{idx + 1} / {currentQs.length}</div>
              <div className="sun-question-text">{renderLekhiText(q.question, `q${q.id}`)}</div>
              {['A', 'B', 'C', 'D'].map(letter => {
                if (!q.options || !q.options[letter]) return null;
                return (
                  <div key={letter}
                    className={`sun-option ${answers[q.id] === letter ? 'selected' : ''}`}
                    onClick={() => handleAnswerSelect(q.id, letter)}>
                    <span className="sun-option-letter">{letter}.</span>
                    <span className="sun-option-text">{renderLekhiText(q.options[letter], `o${q.id}${letter}`)}</span>
                  </div>
                );
              })}
            </div>
          ))}

          <div className="sun-nav-row">
            {!isFirstSubject && (
              <button className="sun-btn sun-btn-secondary"
                onClick={handlePrevSubject} disabled={submitting}>
                {t('prevBtn')}
              </button>
            )}
            {!isLastSubject && (
              <button className="sun-btn sun-btn-primary"
                onClick={handleNextSubject} disabled={submitting}>
                {t('nextBtn')}
              </button>
            )}
            {isLastSubject && (
              <button className="sun-btn sun-btn-submit"
                onClick={handleSubmitClick} disabled={submitting}>
                {submitting ? t('submitting') : t('submitBtn')}
              </button>
            )}
          </div>
        </div>

        {/* Floating timer */}
        {timerHidden && !lockedTimer ? (
          <button className="sun-timer-icon" onClick={() => setTimerHidden(false)}>
            🕐
          </button>
        ) : (
          <div className={`sun-timer-pill ${remainingSec <= 300 ? 'warning' : ''}`}>
            <span className="sun-timer-text">{formatTime(remainingSec)}</span>
            {!lockedTimer && (
              <button className="sun-timer-hide-btn" onClick={() => setTimerHidden(true)}>
                ✕
              </button>
            )}
          </div>
        )}

        {/* Quit confirm modal */}
        {showQuitConfirm && (
          <div className="sun-overlay">
            <div className="sun-modal">
              <div className="sun-modal-title">{t('quitConfirmTitle')}</div>
              <p className="sun-modal-body">{t('quitConfirmBody')}</p>
              <div className="sun-modal-buttons">
                <button className="sun-btn sun-btn-submit" onClick={handleQuitNo}>
                  {t('quitNo')}
                </button>
                <button className="sun-btn sun-btn-secondary" onClick={handleQuitYes}>
                  {t('quitYes')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submit confirm modal */}
        {showSubmitConfirm && (() => {
          const unansweredCount = flatQuestions.filter(q => !answers[q.id]).length;
          return (
            <div className="sun-overlay">
              <div className="sun-modal">
                <div className="sun-modal-title">{t('submitConfirmTitle')}</div>
                <p className="sun-modal-body">
                  {unansweredCount} {t('submitConfirmBody')}
                </p>
                <div className="sun-modal-buttons">
                  <button className="sun-btn sun-btn-secondary" onClick={() => setShowSubmitConfirm(false)}>
                    {t('submitNo')}
                  </button>
                  <button className="sun-btn sun-btn-submit" onClick={handleSubmitConfirm}>
                    {t('submitYes')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Time up popup */}
        {showTimeUpPopup && (
          <div className="sun-overlay">
            <div className="sun-modal">
              <div className="sun-modal-title">{t('timeUpTitle')}</div>
              <p className="sun-modal-body">{t('timeUpBody')}</p>
              <div className="sun-modal-buttons">
                <button className="sun-btn sun-btn-submit" onClick={handleTimeUpSubmit}>
                  {t('timeUpSubmit')}
                </button>
                {!extensionUsed && (
                  <button className="sun-btn sun-btn-secondary" onClick={handleExtend}>
                    {t('timeUpExtend')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Auto-submitted notice */}
        {showAutoSubmitted && (
          <div className="sun-overlay">
            <div className="sun-modal">
              <div className="sun-modal-title">{t('autoSubmittedTitle')}</div>
              <p className="sun-modal-body">{t('autoSubmittedBody')}</p>
              <div className="sun-modal-buttons">
                <button className="sun-btn sun-btn-primary" onClick={handleAutoSubmittedOk}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== RESULTS SCREEN =====
  if (stage === 'results' && results) {
    return (
      <div className={PROTECTED_CONTAINER_CLASS} {...lekhiProtectProps}>
        <LekhiWatermark />
        <div className="sun-header">
          <div style={{ width: 36 }} />
          <div className="sun-header-title">{t('resultsTitle')}</div>
          <div style={{ width: 36 }} />
        </div>
        <div className="sun-content">
          <div className="sun-results-card">
            <div className="sun-results-total">
              {t('totalScore')}: <strong>{results.totalScore} / {flatQuestions.length}</strong>
            </div>
            <table className="sun-results-table">
              <thead>
                <tr>
                  <th>{t('thSubject')}</th>
                  <th>{t('thScore')}</th>
                </tr>
              </thead>
              <tbody>
                {SUBJECTS.map(s => (
                  <tr key={s}>
                    <td>{getSubjectName(s, language)}</td>
                    <td>{results.subjectScores?.[s] || 0} / {questionsBySubject?.[s]?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="sun-results-buttons">
              <button className="sun-btn sun-btn-secondary" onClick={handleReviewStart}>
                {t('reviewBtn')}
              </button>
              <button className="sun-btn sun-btn-primary" onClick={handleResultsGoHome}>
                {t('goHomeBtn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== REVIEW SCREEN =====
  if (stage === 'review' && results) {
    const reviewSubject = SUBJECTS[reviewSubjectIndex];
    const reviewQs = questionsBySubject?.[reviewSubject] || [];
    const isFirstReviewSub = reviewSubjectIndex === 0;
    const isLastReviewSub = reviewSubjectIndex === SUBJECTS.length - 1;

    return (
      <div className={PROTECTED_CONTAINER_CLASS} {...lekhiProtectProps}>
        <LekhiWatermark />
        <div className="sun-header">
          <div style={{ width: 36 }} />
          <div className="sun-header-title">
            {getSubjectName(reviewSubject, language)}
          </div>
          <div style={{ width: 36 }} />
        </div>
        <div className="sun-content">
          {reviewQs.map((q, idx) => {
            const userAns = answers[q.id];
            const correctAns = q.correctAnswer;
            const isCorrect = userAns === correctAns;
            const isEmpty = !userAns;
            return (
              <div key={q.id} className="sun-review-card">
                <div className="sun-review-qnum">{idx + 1} / {reviewQs.length}</div>
                <div className="sun-review-qtext">{renderLekhiText(q.question, `rq${q.id}`)}</div>

                <div className="sun-review-row">
                  <span className="sun-review-label">{t('yourAnswer')}:</span>
                  {isEmpty ? (
                    <span className="sun-review-empty">{t('emptyAnswer')}</span>
                  ) : (
                    <span className={`sun-review-answer ${isCorrect ? 'correct' : 'wrong'}`}>
                      {userAns}. {renderLekhiText(q.options?.[userAns] || '', `ru${q.id}`)}
                    </span>
                  )}
                </div>

                {!isCorrect && (
                  <div className="sun-review-row">
                    <span className="sun-review-label">{t('correctAnswer')}:</span>
                    <span className="sun-review-answer correct">
                      {correctAns}. {renderLekhiText(q.options?.[correctAns] || '', `rc${q.id}`)}
                    </span>
                  </div>
                )}

                {q.explanation && (
                  <div className="sun-review-explanation">
                    <strong>{t('explanation')}:</strong> {renderLekhiText(q.explanation, `re${q.id}`)}
                  </div>
                )}
              </div>
            );
          })}

          <div className="sun-nav-row">
            {!isFirstReviewSub && (
              <button className="sun-btn sun-btn-secondary"
                onClick={() => { setReviewSubjectIndex(i => i - 1); window.scrollTo({ top: 0 }); }}>
                {t('reviewPrevBtn')}
              </button>
            )}
            {!isLastReviewSub && (
              <button className="sun-btn sun-btn-primary"
                onClick={() => { setReviewSubjectIndex(i => i + 1); window.scrollTo({ top: 0 }); }}>
                {t('reviewNextBtn')}
              </button>
            )}
            <button className="sun-btn sun-btn-submit" onClick={handleReviewDone}>
              {t('reviewDoneBtn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}